import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { exchangeCodeForToken } from '../../lib/integrations/facebook/api';
import { supabase } from '../../lib/supabase';
import { exchangeCodeForToken as exchangeGoogleCode } from '../../lib/integrations/google/api';
import { exchangeCodeForToken as exchangeLinkedInCode } from '../../lib/integrations/linkedin/api';
import { exchangeCodeForToken as exchangeInstagramCode } from '../../lib/integrations/instagram/api';

export default function ConnectionCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const processedRef = useRef(false);
  
  useEffect(() => {
    if (!processedRef.current) {
      processedRef.current = true;
      handleCallback();
    }
  }, []);

  const handleCallback = async () => {
    try {
      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      const stateParam = searchParams.get('state');

      // Decode and parse the state parameter
      const state = JSON.parse(atob(stateParam));
      const { service, connectionId } = state;

      console.log(`${service} callback received:`, {
        code,
        error,
        errorDescription,
        stateParam
      });

      // Handle LinkedIn error responses
      if (error) {
        console.error('OAuth error:', error, errorDescription);
        throw new Error(decodeURIComponent(errorDescription) || error);
      }

      if (!code || !stateParam) {
        throw new Error('Missing required parameters');
      }

      if (!service || !connectionId) {
        throw new Error('Invalid state parameter');
      }

      // Handle different OAuth providers
      if (service === 'linkedin_profile') {
        const redirectUri = `${window.location.origin}/connections/callback`;
        console.log('Exchanging code for token via N8N...');
        const tokenData = await exchangeLinkedInCode(code, redirectUri);

        if (!tokenData.success) {
          throw new Error(tokenData.error || 'LinkedIn authentication failed');
        }

        console.log('Token exchange successful:', tokenData);

        // First get the existing connection
        const { data: existingConnection, error: fetchError } = await supabase
          .from('channels')
          .select('*')
          .eq('id', connectionId)
          .single();

        if (fetchError) throw fetchError;

        // Then update it with the new data
        const { data: connection, error: updateError } = await supabase
          .from('channels')
          .update({
            credentials: {
              accessToken: tokenData.data.accessToken,
              profile: {
                id: tokenData.data.profile.id
              }
            },
            settings: {
              name: existingConnection.name, // Use the existing connection name
              profileId: tokenData.data.profile.id,
              given_name: tokenData.data.profile.givenName,
              family_name: tokenData.data.profile.familyName,
              email: tokenData.data.profile.email,
              picture: tokenData.data.profile.picture,
              locale: tokenData.data.profile.locale
            },
            is_configured: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', connectionId)
          .select()
          .single();

        if (updateError) {
          console.error('Error updating connection:', updateError);
          throw updateError;
        }

        console.log('LinkedIn connection updated successfully');
      } else if (service === 'facebook') {
        const redirectUri = `${window.location.origin}/connections/callback`;
        const tokenData = await exchangeCodeForToken(code, redirectUri);

        // Update the connection with the token data and available pages
        const { error } = await supabase
          .from('channels')
          .update({
            credentials: {
              accessToken: tokenData.accessToken,
              expiresIn: tokenData.expiresIn,
              userId: tokenData.userId,
              userName: tokenData.userName,
              userEmail: tokenData.userEmail
            },
            settings: {
              availablePages: tokenData.pages.map(page => ({
                id: page.id,
                name: page.name,
                category: page.category,
                accessToken: page.accessToken
              }))
            },
            is_configured: true,  // Set configured status
            setup_required: false, // No longer requires setup
            last_tested_at: new Date().toISOString(), // Update last tested timestamp
            updated_at: new Date().toISOString()
          })
          .eq('id', connectionId);

        if (error) throw error;

        console.log('Connection updated with token data and pages:', {
          userId: tokenData.userId,
          userName: tokenData.userName,
          pagesCount: tokenData.pages.length
        });
      } else if (service === 'google_analytics') {
        const redirectUri = `${window.location.origin}/connections/callback`;
        const tokenData = await exchangeGoogleCode(code, redirectUri);

        // Update the connection with the token data and available properties
        const { error } = await supabase
          .from('channels')
          .update({
            credentials: {
              accessToken: tokenData.accessToken,
              refreshToken: tokenData.refreshToken,
              expiresIn: tokenData.expiresIn,
              userId: tokenData.userId,
              userName: tokenData.userName,
              userEmail: tokenData.userEmail
            },
            settings: {
              availableProperties: tokenData.properties.map(property => ({
                id: property.id,
                name: property.name,
                createTime: property.createTime,
                updateTime: property.updateTime
              }))
            },
            is_configured: true,
            setup_required: false,
            last_tested_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', connectionId);

        if (error) throw error;
      } else if (service === 'instagram_page') {
        const redirectUri = `${window.location.origin}/connections/callback`;
        console.log('Exchanging code for Instagram token...');
        const tokenData = await exchangeInstagramCode(code, redirectUri);

        if (!tokenData.success) {
          throw new Error(tokenData.error || 'Instagram authentication failed');
        }

        console.log('Instagram token exchange successful:', tokenData);

        // First get the existing connection
        const { data: existingConnection, error: fetchError } = await supabase
          .from('channels')
          .select('*')
          .eq('id', connectionId)
          .single();

        if (fetchError) throw fetchError;

        // Then update it with the new data
        const { error: updateError } = await supabase
          .from('channels')
          .update({
            credentials: {
              accessToken: tokenData.data.accessToken,
              expiresIn: tokenData.data.expiresIn
            },
            settings: {
              name: existingConnection.name,
              availableAccounts: tokenData.data.accounts.map(account => ({
                id: account.id,
                name: account.name,
                username: account.username,
                picture: account.picture,
                pageId: account.pageId,
                pageAccessToken: account.pageAccessToken
              }))
            },
            is_configured: false, // Not configured until account is selected
            setup_required: true, // Require setup to select Instagram account
            last_tested_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', connectionId);

        if (updateError) {
          console.error('Error updating connection:', updateError);
          throw updateError;
        }

        console.log('Instagram connection updated successfully');
      }

      addToast('Connection authenticated successfully', 'success');
      navigate(`/connections/${connectionId}`);
    } catch (error) {
      console.error('Error in OAuth callback:', error);
      addToast('Failed to authenticate connection: ' + error.message, 'error');
      navigate('/connections');
    }
  };

  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary"></div>
    </div>
  );
} 